import { useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { render, pretty } from "@react-email/render";

import ContactTemplate from "./ContactTemplate";

type Inputs = {
  name: string;
  email: string;
  topic: string;
  message: string;
};

const ContactForm = () => {
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [submitTitle, setSubmitTitle] = useState<string>();
  const [submitMessage, setSubmitMessage] = useState<string>();

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<Inputs>();

  const closeModal = () => {
    setIsOpen(false);
  };

  const openModal = () => {
    setIsOpen(true);
    setLoading(false);
  };

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    setLoading(true);

    const emailHtml = await pretty(
      await render(
        <ContactTemplate
          name={data.name}
          email={data.email}
          topic={data.topic}
          message={data.message}
        />,
      ),
    );

    await fetch("https://next-api.useplunk.com/v1/send", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: import.meta.env.VITE_EMAIL_API,
      },
      body: JSON.stringify({
        to: import.meta.env.VITE_EMAIL_ADDRESS,
        reply: data.email,
        subject: "New Contact Submission",
        body: emailHtml,
        from: {
          name: "Provido Contact Form",
          email: "contact@provido.com.au",
        },
      }),
    })
      .then(() => {
        setSubmitTitle("Thank You");
        setSubmitMessage(
          "Thank you for contacting Provido. We'll be in touch as soon as possible.",
        );
        openModal();
      })
      .catch((error) => {
        console.log(error);
        setSubmitTitle("Something Went Wrong");
        setSubmitMessage(
          "Looks like something went wrong. Please try again or email us directly using the email addresses on this page. Sorry for any inconvenience.",
        );
        openModal();
      });
  };

  return (
    <>
      {/* Contact Form */}
      <div>
        <h2 className="pb-2 font-display text-2xl text-blue">Contact Form</h2>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <div>
            <label htmlFor="name" className="label">
              Name:{" "}
              <span className="text-red-500">
                * {errors.name && <span>Name is required.</span>}
              </span>
            </label>
            <input
              type="text"
              {...register("name", { required: true })}
              className="form-field"
            />
          </div>
          <div>
            <label htmlFor="email" className="label">
              Email:{" "}
              <span className="text-red-500">
                * {errors.email && <span>Email is required.</span>}
              </span>
            </label>
            <input
              type="email"
              {...register("email", {
                required: true,
                pattern: /[^\s@]+@[^\s@]+\.[^\s@]+/,
              })}
              className="form-field"
            />
          </div>
          <div>
            <label htmlFor="topic" className="label">
              What are you contacting us about?{" "}
              <span className="text-red-500">
                * {errors.topic && <span>Field is required.</span>}
              </span>
            </label>
            <select
              {...register("topic", { required: true })}
              className="form-field"
            >
              <option hidden selected>
                Please choose a topic
              </option>
              <option value="Support with Daily Living">
                Support with Daily Living
              </option>
              <option value="Community Access">Community Access</option>
              <option value="Nursing">Nursing</option>
              <option value="Transport">Transport</option>
              <option value="Exercise Physiology">Exercise Physiology</option>
              <option value="Supported Independent Living">
                Supported Independent Living
              </option>
              <option value="Multiple Supports">Multiple Supports</option>
              <option value="Something Else">Something Else</option>
            </select>
          </div>
          <div>
            <label htmlFor="message" className="label">
              Message:{" "}
              <span className="text-red-500">
                * {errors.message && <span>Message is required.</span>}
              </span>
            </label>
            <textarea
              placeholder="So we can help you as soon as possible, please provide as much detail as you can."
              {...register("message", { required: true })}
              className="form-field h-32"
            />
          </div>
          <div className="flex flex-row justify-between">
            <button
              type="submit"
              disabled={loading}
              className="w-fit rounded-xl bg-blue px-4 py-2 font-semibold text-white transition hover:cursor-pointer hover:bg-darkblue disabled:bg-blue/10 hover:disabled:bg-blue/10"
            >
              Submit
            </button>
            <button
              type="reset"
              className="hover:bg-grey w-fit rounded-xl bg-gray-200 px-4 py-2 font-semibold hover:cursor-pointer hover:bg-gray-400 hover:text-white"
            >
              Reset
            </button>
          </div>
        </form>
      </div>

      {/* Submission Dialog Box */}
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        className="relative z-50"
      >
        <div className="fixed top-0 left-0 z-49 h-screen w-screen bg-lightblue/20"></div>
        <div className="fixed inset-0 z-50 flex w-screen items-center justify-center">
          <DialogPanel className="max-w-lg space-y-4 rounded-xl border-2 border-lightblue bg-white p-6">
            <DialogTitle className="pb-2 font-display text-2xl text-blue">
              {submitTitle}
            </DialogTitle>
            <p className="text-grey font-body">{submitMessage}</p>
            <div className="flex justify-end">
              <button
                className="w-fit rounded-xl bg-blue px-4 py-2 font-semibold text-white transition hover:cursor-pointer hover:bg-darkblue"
                onClick={() => closeModal()}
              >
                Close
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
};

export default ContactForm;
